/**
 * Die Prüfansicht (28.09.2026): links die Artikel des Entwurfs, rechts EIN Artikel so,
 * wie er in Plenty ankommt — aufgeteilt in die Bereiche, die man aus Plenty kennt.
 *
 * Matze: „Die Anlage ist gefühlt im Blindflug. Die Artikel müssen vorher geprüft werden
 * können … viel übersichtlicher." Alles Rechte kommt aus
 * `GET /entwuerfe/:id/artikel/:schluessel/pruefung`, also aus denselben Payloads wie der
 * Lauf. Begründungen stehen im Tooltip, nicht als Text.
 */
import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { euro, zahl } from '../lib/format';
import {
  BilderBearbeiten, EigenschaftenBearbeiten, PreiseBearbeiten, TexteBearbeiten,
} from './ArtikelKorrektur';
import Info from './Info';
import KiPruefungKasten from './KiPruefungKasten';
import type {
  Ampel, ArtikelPruefung, EntwurfArtikel, FehlendeAttributwerte,
  KorrekturBereich, SchreibbildFeld, SchreibbildVariante,
} from '../lib/typen';

type JeArtikelFehlend = FehlendeAttributwerte['jeArtikel'];

/** Für `<img src>` — das geht nicht über den api-Client, braucht also die Basis selbst. */
const API_BASIS = (import.meta as any).env?.VITE_API_URL || '/api';

type Reiter = 'varianten' | 'preise' | 'texte' | 'eigenschaften' | 'bilder' | 'kanaele';
const REITER: Array<[Reiter, string]> = [
  ['varianten', 'Varianten'], ['preise', 'Preise'], ['texte', 'Texte'],
  ['eigenschaften', 'Kategorie & Eigenschaften'], ['bilder', 'Bilder'], ['kanaele', 'Kanäle'],
];

export default function PruefAnsicht({
  entwurfId, artikel, neuanlage, anwaehlbar, waehltAus, aufAuswahl, auswahlKennung, aufListeNeu,
}: {
  /** Ändert sich mit jedem Häkchen — die Gemini-Leiste zählt die ANGEHAKTEN neu. */
  auswahlKennung: string;
  /** Artikelliste neu laden (z. B. nach „Kategorie passt"). */
  aufListeNeu: () => void;
  entwurfId: number;
  /** Bereits gefiltert (Vorsortierung darüber). */
  artikel: EntwurfArtikel[];
  neuanlage: boolean;
  anwaehlbar: (a: { status: string; ampelBild: string | null }) => boolean;
  waehltAus: boolean;
  aufAuswahl: (schluessel: string, ausgewaehlt: boolean) => void;
}) {
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [daten, setDaten] = useState<ArtikelPruefung | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [reiter, setReiter] = useState<Reiter>('varianten');
  const [fehlend, setFehlend] = useState<FehlendeAttributwerte | null>(null);
  /** Zählt hoch, wenn sich Plenty-Stammdaten geändert haben → Artikel neu laden. */
  const [stand] = useState(0);
  /** Zählt hoch nach einer Korrektur am Artikel → nur den Artikel neu laden. */
  const [artikelStand, setArtikelStand] = useState(0);
  const [speicherFehler, setSpeicherFehler] = useState<string | null>(null);
  /** Hauptbild je Artikel für die Auswahlspalte (Matze 30.09.: „schnelle Entscheidung"). */
  const [hauptbilder, setHauptbilder] = useState<Record<string, { ordner: string; datei: string }>>({});
  useEffect(() => {
    let abgebrochen = false;
    api.get<{ data: Record<string, { ordner: string; datei: string }> }>(`/programmes/artikelimport/entwuerfe/${entwurfId}/hauptbilder`)
      .then((a: { data: { data: Record<string, { ordner: string; datei: string }> } }) => { if (!abgebrochen) setHauptbilder(a.data.data ?? {}); })
      .catch(() => { if (!abgebrochen) setHauptbilder({}); });
    return () => { abgebrochen = true; };
  }, [entwurfId, artikelStand]);

  const speichere = useCallback(async (bereich: KorrekturBereich, wert: unknown) => {
    if (!gewaehlt) return;
    setSpeicherFehler(null);
    try {
      await api.put(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/artikel/korrektur`,
        // Schlüssel im Body, nicht im Pfad — „1/4 Zip Top" enthält ein „/" (→ 404).
        { schluessel: gewaehlt, bereich, wert },
      );
      setArtikelStand(n => n + 1);
    } catch (err: any) {
      setSpeicherFehler(err?.response?.data?.error?.message ?? 'Speichern fehlgeschlagen.');
      throw err;
    }
  }, [entwurfId, gewaehlt]);

  useEffect(() => {
    let abgebrochen = false;
    api.get<{ data: FehlendeAttributwerte }>(`/programmes/artikelimport/entwuerfe/${entwurfId}/attributwerte-fehlend`)
      .then(a => { if (!abgebrochen) setFehlend(a.data.data); })
      .catch(() => { if (!abgebrochen) setFehlend(null); });
    return () => { abgebrochen = true; };
  }, [entwurfId, stand]);

  // Vorwahl: der erste anlegbare Artikel — sonst der erste überhaupt.
  useEffect(() => {
    if (gewaehlt && artikel.some(a => a.artikelSchluessel === gewaehlt)) return;
    const erster = artikel.find(a => anwaehlbar(a)) ?? artikel[0];
    setGewaehlt(erster?.artikelSchluessel ?? null);
  }, [artikel, gewaehlt, anwaehlbar]);

  useEffect(() => {
    if (!gewaehlt) { setDaten(null); return; }
    let abgebrochen = false;
    setLaedt(true);
    setFehler(null);
    setSpeicherFehler(null);
    api.get<{ data: ArtikelPruefung }>(
      `/programmes/artikelimport/entwuerfe/${entwurfId}/artikel/pruefung`,
      { params: { schluessel: gewaehlt } },
    ).then(a => { if (!abgebrochen) setDaten(a.data.data); })
      .catch(err => { if (!abgebrochen) setFehler(err?.response?.data?.error?.message ?? 'Vorschau nicht ladbar.'); })
      .finally(() => { if (!abgebrochen) setLaedt(false); });
    return () => { abgebrochen = true; };
  }, [entwurfId, gewaehlt, stand, artikelStand]);

  const blaettern = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const i = artikel.findIndex(a => a.artikelSchluessel === gewaehlt);
    const n = e.key === 'ArrowDown' ? Math.min(i + 1, artikel.length - 1) : Math.max(i - 1, 0);
    if (artikel[n]) setGewaehlt(artikel[n].artikelSchluessel);
  }, [artikel, gewaehlt]);

  const jeArtikel = fehlend?.jeArtikel ?? {};

  return (
    <div className="space-y-3">
    {fehlend && fehlend.werte.length > 0 && (
      <FehlendeWerte fehlend={fehlend} />
    )}
    {neuanlage && <KiPruefungKasten entwurfId={entwurfId} stand={`${artikelStand}|${auswahlKennung}`} aufFertig={() => setArtikelStand(n => n + 1)} />}
    <div className="grid min-h-[640px] grid-cols-[340px_minmax(0,1fr)] overflow-hidden rounded-lg border border-[#DAD9D9] bg-white">
      {/* ── Liste ── */}
      <div
        className="max-h-[760px] overflow-y-auto border-r border-[#DAD9D9] outline-none"
        tabIndex={0}
        onKeyDown={blaettern}
        aria-label="Artikel — mit ↑ ↓ blättern"
      >
        {artikel.map(a => {
          const aktiv = a.artikelSchluessel === gewaehlt;
          const darf = anwaehlbar(a);
          return (
            <div
              key={a.artikelSchluessel}
              className={`flex items-center gap-3 border-l-[3px] px-3 py-2 ${
                aktiv ? 'border-[#1A1A1A] bg-[#F4F4F1]' : 'border-transparent hover:bg-[#F7F7F4]'
              }`}
            >
              <input
                type="checkbox"
                aria-label={`${a.name} ${neuanlage ? 'anlegen' : 'aktualisieren'}`}
                className="h-4 w-4 shrink-0 accent-[#1A1A1A]"
                checked={a.ausgewaehlt}
                disabled={!darf || waehltAus}
                onChange={() => aufAuswahl(a.artikelSchluessel, !a.ausgewaehlt)}
              />
              <button
                type="button"
                onClick={() => setGewaehlt(a.artikelSchluessel)}
                className={`flex min-w-0 flex-1 items-center gap-2 text-left ${darf ? '' : 'opacity-50'}`}
              >
                <Punkt ampel={zeilenAmpel(a, jeArtikel)} />
                <MiniBild bild={hauptbilder[a.artikelSchluessel]} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-[#1A1A1A]">{a.name}</span>
                  <span className={`block truncate text-xs ${jeArtikel[a.artikelSchluessel]?.blockiert && a.status !== 'uebersprungen' ? 'text-red-800' : 'text-[#6B6A66]'}`}>
                    {zeilenText(a, neuanlage, jeArtikel)}
                  </span>
                </span>
                <span className="text-xs tabular-nums text-[#6B6A66]">{zahl(a.varianten)}</span>
              </button>
            </div>
          );
        })}
        {artikel.length === 0 && <p className="p-4 text-sm text-[#6B6A66]">Keine Artikel in dieser Gruppe.</p>}
      </div>

      {/* ── Artikel ── */}
      <div className="min-w-0 bg-[#F4F4F1]">
        {laedt && !daten && (
          <p className="flex items-center gap-2 p-8 text-sm text-[#6B6A66]">
            <Loader2 className="h-4 w-4 animate-spin" /> Artikel wird aufgebaut …
          </p>
        )}
        {fehler && <p className="m-6 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">{fehler}</p>}
        {daten && (
          <div className={laedt ? 'opacity-60 transition-opacity' : ''}>
            <Kopf daten={daten} neuanlage={neuanlage} />
            <nav className="flex gap-1 border-b border-[#DAD9D9] bg-white px-6" aria-label="Bereiche">
              {REITER.map(([schluessel, name]) => (
                <button
                  key={schluessel}
                  type="button"
                  onClick={() => setReiter(schluessel)}
                  className={`h-11 border-b-2 px-3 text-sm ${
                    reiter === schluessel
                      ? 'border-[#1A1A1A] font-semibold text-[#1A1A1A]'
                      : 'border-transparent text-[#6B6A66] hover:text-[#1A1A1A]'
                  }`}
                >
                  {name}
                </button>
              ))}
            </nav>
            {/* key je Artikel: Meldungen, Fehler und offene Eingaben der Reiter gehören zu
                genau einem Artikel. Ohne key blieben sie beim Wechsel stehen — am 14.10. zeigte
                die Badepantolette die Regel-Meldung der zuvor bearbeiteten Multifunktionsjacke. */}
            <div key={daten.artikelSchluessel} className="space-y-4 p-6">
              {reiter === 'varianten' && <Varianten daten={daten} />}
              {speicherFehler && <p className="rounded border border-red-300 bg-red-50 p-2 text-sm text-red-900">{speicherFehler}</p>}
              {reiter === 'preise' && <PreiseBearbeiten daten={daten} speichere={speichere} />}
              {reiter === 'texte' && (
                <TexteBearbeiten daten={daten} speichere={speichere} entwurfId={entwurfId} aufNeuLaden={() => setArtikelStand(n => n + 1)} />
              )}
              {reiter === 'eigenschaften' && (
                <EigenschaftenBearbeiten daten={daten} entwurfId={entwurfId} speichere={speichere} aufNeuLaden={() => setArtikelStand(n => n + 1)} aufListeNeu={aufListeNeu} />
              )}
              {reiter === 'bilder' && <BilderBearbeiten daten={daten} speichere={speichere} bildUrl={b => bildUrl(b, daten)} />}
              {reiter === 'kanaele' && <Kanaele daten={daten} />}
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}

// ==================== Neue Werte ====================

/**
 * Farben/Größen, die es in Plenty noch nicht gibt. Matze 28.09.2026: „das muss immer
 * durch das Tool passieren … da muss dann sowas stehen wie wird angelegt." Der Lauf legt
 * sie vor dem Anlegen selbst an — hier steht nur, WELCHE neu dazukommen. Kein Knopf.
 */
function FehlendeWerte({ fehlend }: { fehlend: FehlendeAttributwerte }) {
  const n = fehlend.werte.length;
  return (
    <div className="rounded-lg border border-[#DAD9D9] bg-white px-4 py-3 text-sm text-[#1A1A1A]">
      <div className="flex flex-wrap items-center gap-1.5">
        <b className="mr-1">{zahl(n)} {n === 1 ? 'Wert wird' : 'Werte werden'} in Plenty neu angelegt:</b>
        {fehlend.werte.map(w => (
          <span key={`${w.attributId}|${w.wert}`} className="rounded-full bg-[#E9FF57] px-2.5 py-0.5 text-xs" title={`${w.attribut} · ${zahl(w.artikel.length)} Artikel`}>
            {w.wert}
          </span>
        ))}
        <Info breit titel="Welche Artikel">
          <p>Beim Anlegen legt das Tool diese Farben/Größen zuerst in Plenty an, dann die Artikel.</p>
          <table className="w-full">
            <tbody>
              {fehlend.werte.map(w => (
                <tr key={`${w.attributId}|${w.wert}`} className="border-t border-[#ECECE8]">
                  <td className="py-0.5 pr-2 text-[#6B6A66]">{w.attribut}</td>
                  <td className="py-0.5 pr-2">{w.wert}</td>
                  <td className="py-0.5 text-right text-[#6B6A66]">{zahl(w.artikel.length)} Artikel</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Info>
      </div>
    </div>
  );
}

// ==================== Liste ====================

function zeilenAmpel(a: EntwurfArtikel, fehlend: JeArtikelFehlend): Ampel {
  const f = a.status === 'uebersprungen' ? undefined : fehlend[a.artikelSchluessel];
  if (f?.blockiert) return 'rot';
  if (a.ampelKategorie === 'rot' || a.ampelBild === 'rot') return 'rot';
  if (a.ampelKategorie === 'gelb' || a.ampelBild === 'gelb') return 'gelb';
  return 'gruen';
}

function zeilenText(a: EntwurfArtikel, neuanlage: boolean, fehlend: JeArtikelFehlend): string {
  if (a.status === 'uebersprungen') return neuanlage ? 'existiert bereits' : 'in Plenty';
  if (neuanlage && a.ampelBild !== 'gruen' && a.ampelBild !== 'gelb') return 'kein Bild — wird nicht angelegt';
  const f = fehlend[a.artikelSchluessel];
  if (f?.blockiert) return 'nicht anlegbar — Varianten nicht unterscheidbar';
  if (f && neuanlage) return 'wird angelegt · neue Farbe/Größe';
  // Gelber Punkt = das Tool ist unsicher — sagen, worüber (28.09.: „warum orange?").
  if (a.ampelKategorie === 'gelb') return `${neuanlage ? 'wird angelegt' : 'wird aktualisiert'} · Kategorie prüfen`;
  if (a.ampelBild === 'gelb') return `${neuanlage ? 'wird angelegt' : 'wird aktualisiert'} · Bilder unvollständig`;
  if (a.ampelKategorie === 'rot') return 'ohne Kategorie';
  if (a.ampelBild === 'rot') return 'ohne Bild';
  return neuanlage ? 'wird angelegt' : 'wird aktualisiert';
}

function Punkt({ ampel }: { ampel: Ampel | null }) {
  const farbe = ampel === 'rot' ? 'bg-red-600' : ampel === 'gelb' ? 'bg-amber-500' : ampel === 'gruen' ? 'bg-green-600' : 'bg-[#DAD9D9]';
  return <span className={`h-2 w-2 shrink-0 rounded-full ${farbe}`} />;
}

// ==================== Kopf ====================

function Kopf({ daten, neuanlage }: { daten: ArtikelPruefung; neuanlage: boolean }) {
  const sb = daten.schreibbild;
  const haupt = sb.varianten.find(v => v.istHaupt) ?? sb.varianten[0];
  const kinder = sb.varianten.filter(v => !v.istHaupt);
  const farben = new Set(kinder.map(v => attributWert(v, 'farbe')).filter(Boolean)).size;
  const groessen = new Set(kinder.map(v => attributWert(v, 'groesse')).filter(Boolean)).size;
  const bilder = eindeutigeBilder(sb.varianten).length;
  const nichtGesetzt = [...sb.artikel, ...sb.jeVariante].filter(f => f.weggelassen);
  const status = daten.status === 'uebersprungen'
    ? { text: neuanlage ? 'existiert bereits' : 'in Plenty', stil: 'bg-red-50 text-red-900' }
    : daten.nichtAnlegbar.length > 0
      ? { text: 'wird nicht angelegt', stil: 'bg-red-50 text-red-900' }
      : { text: neuanlage ? 'wird angelegt · inaktiv' : 'wird aktualisiert', stil: 'bg-green-50 text-green-900' };
  const fehlt = daten.fehlendeAttributwerte.length;

  return (
    <section className="flex gap-6 border-b border-[#DAD9D9] bg-white px-6 py-5">
      <div className="min-w-0 flex-1 space-y-3">
        <h3 className="text-2xl font-bold text-[#1A1A1A]">{daten.name}</h3>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className={`rounded-full px-3 py-1 font-semibold ${status.stil}`}>{status.text}</span>
          {Object.keys(daten.korrektur ?? {}).some(k => k !== 'ki') && (
            <span className="rounded-full bg-[#E9FF57] px-3 py-1 font-semibold text-[#1A1A1A]" title={Object.keys(daten.korrektur).filter(k => k !== 'ki').join(', ')}>
              von Hand geändert
            </span>
          )}
          {daten.kategorien.length > 0
            ? daten.kategorien.map(k => (
              <span key={k.id} className="rounded-full bg-[#F0F0EC] px-3 py-1" title={`Kategorie ${k.id}`}>
                {k.pfad.split(' > ').slice(-2).join(' › ')}
              </span>
            ))
            : <span className="rounded-full bg-amber-50 px-3 py-1 text-amber-900">ohne Kategorie</span>}
          <span className="rounded-full bg-[#F0F0EC] px-3 py-1">
            {zahl(kinder.length || 1)} Varianten{farben > 0 && ` · ${farben} Farben`}{groessen > 0 && ` · ${groessen} Größen`}
          </span>
          <span className={`rounded-full px-3 py-1 ${bilder === 0 ? 'bg-amber-50 text-amber-900' : 'bg-[#F0F0EC]'}`}>
            {bilder === 0 ? 'ohne Bild' : `${zahl(bilder)} Bilder`}
          </span>
        </div>
        {haupt && (
          <div className="flex flex-wrap gap-6">
            {haupt.preise.map(p => {
              // Mehrere Preise je Preisart = Größenstaffel (Kinder günstiger) — als Spanne zeigen.
              const werte = kinder.flatMap(v => v.preise.filter(x => x.preisart === p.preisart).map(x => x.preis));
              const von = Math.min(p.preis, ...werte);
              const bis = Math.max(p.preis, ...werte);
              return (
                <div key={p.salesPriceId}>
                  <div className="text-xs text-[#6B6A66]">{p.preisart}</div>
                  <div className="text-lg font-bold tabular-nums">
                    {von === bis ? euro(von) : <>{euro(von)} <span className="text-sm font-normal text-[#6B6A66]">bis</span> {euro(bis)}</>}
                  </div>
                </div>
              );
            })}
            {haupt.einkaufspreis != null && (
              <div>
                <div className="text-xs text-[#6B6A66]">EK</div>
                <div className="text-lg font-semibold tabular-nums text-[#6B6A66]">{euro(haupt.einkaufspreis)}</div>
              </div>
            )}
          </div>
        )}
        {daten.nichtAnlegbar.length > 0 && (
          <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-900" title={daten.nichtAnlegbar.join('\n')}>
            Wird nicht angelegt — Varianten nicht unterscheidbar
          </p>
        )}
        {daten.nichtAnlegbar.length === 0 && (daten.farbenOhneBild?.length ?? 0) > 0 && (
          <p className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-900" title={daten.farbenOhneBild.map(f => `${f.schluessel.toUpperCase()}: ${f.zeilen} Varianten`).join('\n')}>
            {daten.farbenOhneBild.length === 1 ? 'Eine Farbe ohne Bild' : `${zahl(daten.farbenOhneBild.length)} Farben ohne Bild`}
            {' '}({daten.farbenOhneBild.map(f => f.schluessel.toUpperCase()).join(', ')}) — {zahl(daten.farbenOhneBild.reduce((n, f) => n + f.zeilen, 0))} Varianten werden nicht angelegt.
          </p>
        )}
        {daten.nichtAnlegbar.length === 0 && fehlt > 0 && (
          <p className="text-sm text-[#1A1A1A]" title={daten.fehlendeAttributwerte.join('\n')}>
            <span className="mr-1 rounded bg-[#E9FF57] px-1.5 py-0.5 text-xs font-semibold">neu</span>
            {zahl(fehlt)} {fehlt === 1 ? 'Farbe/Größe wird' : 'Farben/Größen werden'} beim Anlegen in Plenty angelegt.
          </p>
        )}
      </div>
      {nichtGesetzt.length > 0 && (
        <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
          <span className="text-[#6B6A66]">Nicht gesetzt</span>
          {nichtGesetzt.map(f => (
            <span key={f.feld} title={f.hinweis ?? f.wert} className="rounded border border-dashed border-[#B7B6B1] px-2 py-0.5 text-[#6B6A66]">
              {f.feld}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

// ==================== Reiter ====================

function Karte({ titel, children, className = '' }: { titel: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`space-y-2 rounded-lg border border-[#DAD9D9] bg-white p-4 ${className}`}>
      <div className="text-xs text-[#6B6A66]">{titel}</div>
      {children}
    </div>
  );
}

function Varianten({ daten }: { daten: ArtikelPruefung }) {
  const varianten = daten.schreibbild.varianten;
  const preisarten = varianten[0]?.preise.map(p => p.preisart) ?? [];
  const fest = daten.schreibbild.jeVariante.filter(f => !f.weggelassen && ['Einheit', 'Hauptlager', 'Lieferzeit-Anzeige'].includes(f.feld));
  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-[#DAD9D9] bg-white">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-[#6B6A66]">
            <tr className="border-b border-[#ECECE8]">
              <th className="px-3 py-2 font-normal">Farbe</th>
              <th className="px-3 py-2 font-normal">Größe</th>
              <th className="px-3 py-2 font-normal">EAN</th>
              <th className="px-3 py-2 text-right font-normal">Gewicht</th>
              {preisarten.map(p => <th key={p} className="px-3 py-2 text-right font-normal">{p}</th>)}
              <th className="px-3 py-2 font-normal">Bild</th>
            </tr>
          </thead>
          <tbody>
            {varianten.map((v, i) => (
              <tr key={`${v.quellZeile}-${i}`} className="border-b border-[#F2F2EF] last:border-0">
                <td className="px-3 py-2">
                  {v.istHaupt
                    ? <span className="rounded bg-[#1A1A1A] px-2 py-0.5 text-xs text-white" title="Steht für den Artikel — ohne Größe, Farbe und EAN, bleibt inaktiv.">Hauptvariante</span>
                    : <AttributZelle v={v} rolle="farbe" />}
                </td>
                <td className="px-3 py-2">{v.istHaupt ? '' : <AttributZelle v={v} rolle="groesse" />}</td>
                <td className="px-3 py-2 tabular-nums">{v.barcode ?? <span className="text-[#8A8984]">—</span>}</td>
                <td className="px-3 py-2 text-right tabular-nums">{v.gewichtG != null ? `${zahl(v.gewichtG)} g` : '—'}</td>
                {preisarten.map(p => (
                  <td key={p} className="px-3 py-2 text-right tabular-nums">
                    {euro(v.preise.find(x => x.preisart === p)?.preis ?? null)}
                  </td>
                ))}
                <td className="px-3 py-2 text-xs text-[#6B6A66]">{v.bilder[0]?.datei ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#6B6A66]">
        {[
          varianten[0]?.zolltarifnummer ? `Zolltarif ${varianten[0].zolltarifnummer}` : null,
          ...fest.map(f => `${f.feld} ${f.wert}`),
        ].filter(Boolean).join(' · ')}
      </p>
    </>
  );
}

function AttributZelle({ v, rolle }: { v: SchreibbildVariante; rolle: 'farbe' | 'groesse' }) {
  const a = v.attribute.find(x => x.rolle === rolle);
  if (!a) return <span className="text-[#8A8984]">—</span>;
  // valueId < 0 = Platzhalter: den Wert legt der Lauf vorher in Plenty an (Matze 28.09.).
  if (a.valueId != null && a.valueId < 0) return <span>{a.wert}<span className="ml-1 rounded bg-[#E9FF57] px-1 text-[10px] font-semibold text-[#1A1A1A]" title="Gibt es in Plenty noch nicht — wird beim Anlegen angelegt.">neu</span></span>;
  return a.valueId == null
    ? <span className="text-red-800" title="Diesen Wert gibt es in Plenty nicht — die Variante entsteht ohne ihn.">{a.wert} ⚠</span>
    : <span>{a.wert}</span>;
}

function Kanaele({ daten }: { daten: ArtikelPruefung }) {
  const f = (name: string) => [...daten.schreibbild.jeVariante, ...daten.schreibbild.artikel].find(x => x.feld === name);
  const maerkte = f('Märkte')?.wert ?? '';
  const liste = maerkte.includes(' — ') ? maerkte.split(' — ')[1].split(', ') : [];
  const rest = ['Mandant', 'Sichtbarkeit', 'Bestandsregel', 'Amazon', 'Versandprofile', 'Erscheinungsdatum', 'Verfügbar bis', 'Zustand der Variante']
    .map(f).filter((x): x is SchreibbildFeld => x != null);
  return (
    <div className="grid grid-cols-3 gap-4">
      <Karte titel="Einstellungen">
        {rest.map(x => (
          <div key={x.feld} className="flex justify-between gap-3 text-sm" title={x.hinweis}>
            <span className="text-[#6B6A66]">{x.feld}</span>
            <span className={`text-right ${x.weggelassen ? 'text-[#8A8984]' : ''}`}>{x.wert}</span>
          </div>
        ))}
      </Karte>
      <Karte titel={`Märkte ${zahl(liste.length)}`} className="col-span-2">
        <div className="flex flex-wrap gap-2">
          {liste.map(m => <span key={m} className="rounded-full bg-[#F0F0EC] px-3 py-1 text-xs">{m}</span>)}
        </div>
      </Karte>
    </div>
  );
}

// ==================== Hilfen ====================

/** Kleines Hauptbild in der Auswahlspalte — lädt erst, wenn die Zeile sichtbar wird. */
function MiniBild({ bild }: { bild?: { ordner: string; datei: string } }) {
  const [kaputt, setKaputt] = useState(false);
  if (!bild || kaputt) {
    return <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-dashed border-[#DAD9D9] text-[10px] text-[#8A8984]">kein Bild</span>;
  }
  const url = `${API_BASIS}/programmes/artikelimport/bildvorschau?mini=1&ordner=${encodeURIComponent(bild.ordner)}&datei=${encodeURIComponent(bild.datei)}`;
  return (
    <img
      src={url}
      alt=""
      loading="lazy"
      width={48}
      height={48}
      title={bild.datei}
      onError={() => setKaputt(true)}
      className="h-12 w-12 shrink-0 rounded border border-[#ECECE8] bg-white object-contain"
    />
  );
}

/** Bild-URL für `<img src>` — der Ordner fällt auf den einzigen gewählten zurück. */
function bildUrl(b: { datei: string; ordner: string }, daten: ArtikelPruefung): string {
  const ordner = b.ordner || daten.bildOrdner[0] || '';
  return `${API_BASIS}/programmes/artikelimport/bildvorschau?ordner=${encodeURIComponent(ordner)}&datei=${encodeURIComponent(b.datei)}`;
}

function attributWert(v: SchreibbildVariante, rolle: 'farbe' | 'groesse'): string {
  return v.attribute.find(a => a.rolle === rolle)?.wert ?? '';
}

function eindeutigeBilder(varianten: SchreibbildVariante[]) {
  const karte = new Map<string, { datei: string; ordner: string; hauptbild: boolean; farbe: string; varianten: number }>();
  for (const v of varianten) {
    for (const b of v.bilder) {
      const e = karte.get(b.datei);
      if (e) {
        e.varianten++;
        if (!e.farbe) e.farbe = attributWert(v, 'farbe');
      } else {
        karte.set(b.datei, { datei: b.datei, ordner: b.ordner, hauptbild: b.hauptbild, farbe: attributWert(v, 'farbe'), varianten: 1 });
      }
    }
  }
  return Array.from(karte.values());
}
